import Plotly, { ColorScale } from "plotly.js-dist-min";
import { PmmData } from "./PmmData"; // assuming your type definitions are in a file named types.ts

type Scatter3dTrace = {
  type: "scatter3d";
  mode: "lines" | "markers";
  x: number[];
  y: number[];
  z: number[];
  line?: { color: string; width: number };
  marker?: { color: string; size: number };
  hoverinfo?: "skip";
  showLegend?: boolean;
};

type SurfaceTrace = {
  type: "surface";
  x: number[][];
  y: number[][];
  z: number[][];
  opacity?: number;
  colorscale?: ColorScale;
  showscale?: boolean;
};

type ConeTrace = {
  type: "cone";
  x: number[];
  y: number[];
  z: number[];
  u: number[];
  v: number[];
  w: number[];
  sizemode: string;
  colorscale: ColorScale;
  showscale: boolean;
  sizeRef: number;
  hoverinfo?: "skip";
};

// Function to generate the PMM 3D plot
export function plotPmm(pmmData: PmmData, elementId: string, fullScreenMode?: boolean): void {
  const { xVals: X, yVals: Y, zVals: Z, loadCombos } = pmmData;

  // Define colors and labels for axes
  const axisColors = { x: "#FF0000", y: "#00CC00", z: "#0000FF" };
  const axLabels = { x: "ΦMₙₓ", y: "ΦMₙᵧ", z: "ΦPₙ" };

  // Calculate axis ranges and factors
  const zFactor = 0.12;
  const xyFactor = 1.05;
  const minAspect = 2;

  const minMax = [
    [Math.min(...X.flat()), Math.max(...X.flat())],
    [Math.min(...Y.flat()), Math.max(...Y.flat())],
    [Math.min(...Z.flat()), Math.max(...Z.flat())],
  ].map(([min, max]) => ({ min, max, range: max - min }));

  const zRange = minMax[2].range * (1 + 2 * zFactor);
  const zMin = minMax[2].min - zRange * zFactor;
  const zMax = minMax[2].max + zRange * zFactor;

  const maxXYRange = Math.max(minMax[0].range, minMax[1].range);
  const xHalfLength = Math.min(xyFactor * maxXYRange, minAspect * minMax[0].range) / 2;
  const yHalfLength = Math.min(xyFactor * maxXYRange, minAspect * minMax[1].range) / 2;

  // Helper function to create axis arrow
  const createAxisArrow = (axis: "x" | "y" | "z") => {
    const color = axisColors[axis];

    const arrowBody: Scatter3dTrace = {
      type: "scatter3d",
      mode: "lines",
      line: { color, width: 5 },
      marker: { size: 1, color },
      x: axis === "x" ? [-xHalfLength, xHalfLength] : [0, 0],
      y: axis === "y" ? [-yHalfLength, yHalfLength] : [0, 0],
      z: axis === "z" ? [zMin, zMax] : [0, 0],
      hoverinfo: "skip",
      showLegend: false,
    };

    const arrowHead: ConeTrace = {
      type: "cone",
      sizemode: "raw",
      sizeRef: 1,
      x: [axis === "x" ? xHalfLength : 0],
      y: [axis === "y" ? yHalfLength : 0],
      z: [axis === "z" ? zMax : 0],
      u: [axis === "x" ? 0.1 * xHalfLength : 0],
      v: [axis === "y" ? 0.1 * yHalfLength : 0],
      w: [axis === "z" ? 0.05 * zRange : 0],
      colorscale: [
        [0, color],
        [1, color],
      ],
      showscale: false,
      hoverinfo: "skip",
    };

    return [arrowBody, arrowHead];
  };

  const annotationCommon = {
    showarrow: false,
    xanchor: "left" as "left" | "auto" | "center" | "right",
    x: 0,
    y: 0,
    z: 0,
    alwaysdisplay: true,
    font: { size: fullScreenMode ? 18 : 14, color: "#1f1f1f" },
  };

  // Create layout with scene settings
  const layout = {
    // title: {
    //   text: "PMM Diagram",
    //   font: { size: 18, color: "#1f1f1f" },
    //   x: 0.5,
    // },
    scene: {
      xaxis: { visible: false },
      yaxis: { visible: false },
      zaxis: { visible: false },
      annotations: [
        {
          ...annotationCommon,
          text: axLabels.x,
          x: 0.95 * xHalfLength,
          xshift: 8,
        },
        {
          ...annotationCommon,
          text: axLabels.y,
          y: 0.95 * yHalfLength,
          xshift: 8,
        },
        {
          ...annotationCommon,
          text: axLabels.z,
          z: 0.95 * zMax,
          xshift: 2,
        },
      ],
      aspectmode: "cube" as "cube" | "auto" | "data" | "manual",
    },
    width: fullScreenMode ? undefined : 350,
    height: fullScreenMode ? undefined : 350,
    margin: fullScreenMode
      ? { l: 20, r: 20, t: 0, b: 0, autoexpand: false }
      : { l: 5, r: 0, t: 30, b: 10, autoexpand: false },
    // autosize: fullScreenMode ? true : false,
  };

  // Create traces for the 3D PMM plot
  const traces: Array<Scatter3dTrace | SurfaceTrace | ConeTrace> = [
    // Load points
    {
      type: "scatter3d",
      mode: "markers",
      x: loadCombos.map((combo) => combo.mx),
      y: loadCombos.map((combo) => combo.my),
      z: loadCombos.map((combo) => combo.p),
      marker: { color: "#002095", size: 4 },
    },
    // PMM surface
    {
      type: "surface",
      x: X,
      y: Y,
      z: Z,
      opacity: 0.5,
      colorscale: [
        [0, "#ffbb0f"],
        [1, "#ffbb0f"],
      ],
      showscale: false,
    },
    // PMM mesh lines (row and column lines)
    ...X.map(
      (_, i) =>
        ({
          type: "scatter3d",
          mode: "lines",
          line: { color: "#f7f7f7", width: 1.5 },
          x: X[i],
          y: Y[i],
          z: Z[i],
        } as Scatter3dTrace)
    ),
    ...X[0].map(
      (_, j) =>
        ({
          type: "scatter3d",
          mode: "lines",
          line: { color: "#f7f7f7", width: 1.5 },
          x: X.map((row) => row[j]),
          y: Y.map((row) => row[j]),
          z: Z.map((row) => row[j]),
        } as Scatter3dTrace)
    ),
  ];

  // Add axis arrows to traces
  ["x", "y", "z"].forEach((axis) => {
    createAxisArrow(axis as "x" | "y" | "z").forEach((arrow) => traces.push(arrow));
  });

  // Render plot with Plotly
  Plotly.react(elementId, traces, layout, {
    responsive: true,
  });
}
